import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate, useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  ArrowRight, Save, Send, Trash2, Archive, Loader2, FileText, Settings,
  Search as SearchIcon, Image as ImageIcon, AlertTriangle, Plus, X, Tag as TagIcon,
  User,
} from 'lucide-react';
import { blogAPI } from '../../services/api';
import MediaPicker from '../../components/media/MediaPicker';
import PageSeoPanel from '../../components/website-builder/PageSeoPanel';
import BlogCategories from './BlogCategories';

/**
 * BlogPostEditor — Phase 6 full-page editor for a BlogPost.
 *
 * Handles create + edit via URL param :id.
 *  - id === 'new'   → create mode (empty draft, first save via create())
 *  - id === <ObjectId> → edit mode (fetches via blogAPI.get)
 *
 * Uses a tabbed interface: المحتوى | SEO | إعدادات.
 * Reuses PageSeoPanel for the SEO tab.
 *
 * 409 SLUG_CONFLICT maps to "الرابط مستخدم مسبقاً — اختر واحداً آخر".
 */

const SLUG_REGEX = /^[\u0600-\u06FFa-z0-9](?:[\u0600-\u06FFa-z0-9-]*[\u0600-\u06FFa-z0-9])?$/i;

const EMPTY_FORM = {
  slug: '',
  title: { ar: '', en: '' },
  excerpt: { ar: '', en: '' },
  content: { ar: '', en: '' },
  featuredImageUrl: '',
  featuredImageAlt: '',
  author: {
    name: '',
    avatarUrl: '',
    bio: { ar: '', en: '' },
  },
  readTimeMinutes: 0,
  categoryId: '',
  tags: [],
  isFeatured: false,
  sortOrder: 100,
  seo: {
    title: { ar: '', en: '' },
    description: { ar: '', en: '' },
    keywords: [],
    ogImageUrl: '',
    canonicalUrl: '',
    noindex: false,
  },
};

function cloneForm(source) {
  const s = source || {};
  return {
    slug: s.slug || '',
    title: { ar: s.title?.ar || '', en: s.title?.en || '' },
    excerpt: { ar: s.excerpt?.ar || '', en: s.excerpt?.en || '' },
    content: { ar: s.content?.ar || '', en: s.content?.en || '' },
    featuredImageUrl: s.featuredImageUrl || '',
    featuredImageAlt: s.featuredImageAlt || '',
    author: {
      name: s.author?.name || '',
      avatarUrl: s.author?.avatarUrl || '',
      bio: { ar: s.author?.bio?.ar || '', en: s.author?.bio?.en || '' },
    },
    readTimeMinutes: Number(s.readTimeMinutes) || 0,
    categoryId: (s.categoryId && typeof s.categoryId === 'object' ? s.categoryId._id : s.categoryId) || '',
    tags: Array.isArray(s.tags) ? [...s.tags] : [],
    isFeatured: !!s.isFeatured,
    sortOrder: Number.isFinite(Number(s.sortOrder)) ? Number(s.sortOrder) : 100,
    seo: {
      title: { ar: s.seo?.title?.ar || '', en: s.seo?.title?.en || '' },
      description: { ar: s.seo?.description?.ar || '', en: s.seo?.description?.en || '' },
      keywords: Array.isArray(s.seo?.keywords) ? [...s.seo.keywords] : [],
      ogImageUrl: s.seo?.ogImageUrl || '',
      canonicalUrl: s.seo?.canonicalUrl || '',
      noindex: !!s.seo?.noindex,
    },
  };
}

function buildPayload(form) {
  const payload = {
    title: { ar: form.title.ar || '', en: form.title.en || '' },
    excerpt: { ar: form.excerpt.ar || '', en: form.excerpt.en || '' },
    content: { ar: form.content.ar || '', en: form.content.en || '' },
    featuredImageUrl: form.featuredImageUrl || '',
    featuredImageAlt: form.featuredImageAlt || '',
    author: {
      name: form.author.name || '',
      avatarUrl: form.author.avatarUrl || '',
      bio: { ar: form.author.bio.ar || '', en: form.author.bio.en || '' },
    },
    readTimeMinutes: Number(form.readTimeMinutes) || 0,
    categoryId: form.categoryId || null,
    tags: Array.isArray(form.tags) ? form.tags.filter(Boolean) : [],
    isFeatured: !!form.isFeatured,
    sortOrder: Number.isFinite(Number(form.sortOrder)) ? Number(form.sortOrder) : 100,
    seo: {
      title: { ar: form.seo.title.ar || '', en: form.seo.title.en || '' },
      description: { ar: form.seo.description.ar || '', en: form.seo.description.en || '' },
      keywords: Array.isArray(form.seo.keywords) ? form.seo.keywords.filter(Boolean) : [],
      ogImageUrl: form.seo.ogImageUrl || '',
      canonicalUrl: form.seo.canonicalUrl || '',
      noindex: !!form.seo.noindex,
    },
  };
  if (form.slug) payload.slug = form.slug;
  return payload;
}

function mapErrorToArabic(err, fallback) {
  const code = err?.response?.data?.code;
  if (code === 'SLUG_CONFLICT') return 'الرابط مستخدم مسبقاً — اختر واحداً آخر';
  if (code === 'INVALID_SLUG') return 'الرابط غير صالح — حروف وأرقام وشرطات فقط';
  if (code === 'TITLE_OR_SLUG_REQUIRED') return 'أدخل عنواناً أو رابطاً للمقال';
  if (code === 'CAT_NOT_FOUND') return 'التصنيف المحدد غير موجود';
  return err?.response?.data?.message || fallback;
}

function formatRelativeAr(value) {
  if (!value) return '';
  try {
    const d = new Date(value);
    const diffMs = Date.now() - d.getTime();
    const diffMin = Math.round(diffMs / 60000);
    const diffHr = Math.round(diffMin / 60);
    const diffDay = Math.round(diffHr / 24);
    if (Math.abs(diffMin) < 1) return 'قبل لحظات';
    if (Math.abs(diffMin) < 60) return `قبل ${diffMin} د`;
    if (Math.abs(diffHr) < 24) return `قبل ${diffHr} س`;
    if (Math.abs(diffDay) < 30) return `قبل ${diffDay} يوم`;
    return d.toLocaleDateString('ar-SA');
  } catch {
    return '';
  }
}

export default function BlogPostEditor() {
  const navigate = useNavigate();
  const { id: paramId } = useParams();
  const isNew = !paramId || paramId === 'new';

  const [currentId, setCurrentId] = useState(isNew ? null : paramId);
  const [form, setForm] = useState(EMPTY_FORM);
  const [baseline, setBaseline] = useState(EMPTY_FORM);
  const [meta, setMeta] = useState(null);
  const [loading, setLoading] = useState(!isNew);
  const [loadError, setLoadError] = useState(null);

  const [tab, setTab] = useState('content');
  const [contentLocale, setContentLocale] = useState('ar');
  const [authorLocale, setAuthorLocale] = useState('ar');

  const [saving, setSaving] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [archiving, setArchiving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const [pickerField, setPickerField] = useState(null); // 'featured' | 'author'
  const [categoriesOpen, setCategoriesOpen] = useState(false);

  const [categories, setCategories] = useState([]);
  const [tagInput, setTagInput] = useState('');

  const aliveRef = useRef(true);

  useEffect(() => {
    aliveRef.current = true;
    return () => { aliveRef.current = false; };
  }, []);

  // Sync URL id → currentId
  useEffect(() => {
    setCurrentId(isNew ? null : paramId);
  }, [paramId, isNew]);

  const fetchCategories = useCallback(async () => {
    try {
      const resp = await blogAPI.listCategories();
      const data = resp.data?.data || resp.data;
      if (!aliveRef.current) return;
      const list = Array.isArray(data?.items) ? data.items : [];
      list.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
      setCategories(list);
    } catch {
      // non-critical
    }
  }, []);

  const fetchPost = useCallback(async (id) => {
    if (!id) return;
    setLoading(true);
    setLoadError(null);
    try {
      const resp = await blogAPI.get(id);
      const data = resp.data?.data || resp.data;
      if (!aliveRef.current) return;
      const next = cloneForm(data);
      setForm(next);
      setBaseline(next);
      setMeta(data);
    } catch (err) {
      if (!aliveRef.current) return;
      setLoadError(err?.response?.data?.message || 'فشل تحميل المقال');
    } finally {
      if (aliveRef.current) setLoading(false);
    }
  }, []);

  useEffect(() => { fetchCategories(); }, [fetchCategories]);

  useEffect(() => {
    if (!currentId) {
      setForm(EMPTY_FORM);
      setBaseline(EMPTY_FORM);
      setMeta(null);
      setLoading(false);
      return;
    }
    fetchPost(currentId);
  }, [currentId, fetchPost]);

  const dirty = useMemo(
    () => JSON.stringify(form) !== JSON.stringify(baseline),
    [form, baseline]
  );

  const seoDirty = useMemo(
    () => JSON.stringify(form.seo) !== JSON.stringify(baseline.seo),
    [form.seo, baseline.seo]
  );

  const statusLabel = (() => {
    const s = meta?.status;
    if (s === 'published') return 'منشور';
    if (s === 'draft') return 'مسودة';
    if (s === 'archived') return 'مؤرشف';
    return currentId ? 'مسودة' : 'جديد';
  })();

  const statusBadge = (() => {
    const s = meta?.status;
    if (s === 'published' && !dirty) {
      return 'bg-emerald-50 text-emerald-700 border-emerald-200';
    }
    if (s === 'archived') return 'bg-gray-100 text-gray-600 border-gray-200';
    return 'bg-amber-50 text-amber-700 border-amber-200';
  })();

  const validateBeforeSave = useCallback(() => {
    if (!form.title.ar && !form.title.en) {
      toast.error('أدخل عنوان المقال بالعربية أو الإنجليزية');
      return false;
    }
    if (form.slug && !SLUG_REGEX.test(form.slug)) {
      toast.error('الرابط غير صالح — حروف صغيرة وأرقام وشرطات فقط');
      return false;
    }
    return true;
  }, [form]);

  const saveDraft = useCallback(async ({ silent = false } = {}) => {
    if (!validateBeforeSave()) return null;
    setSaving(true);
    try {
      const payload = buildPayload(form);
      let saved;
      if (!currentId) {
        const resp = await blogAPI.create(payload);
        saved = resp.data?.data || resp.data;
        if (!silent) toast.success('تم إنشاء المسودة');
        if (saved?._id) {
          const next = cloneForm(saved);
          if (aliveRef.current) {
            setForm(next);
            setBaseline(next);
            setMeta(saved);
            setCurrentId(saved._id);
          }
          navigate(`/settings/website/blog/${saved._id}`, { replace: true });
        }
      } else {
        const resp = await blogAPI.update(currentId, payload);
        saved = resp.data?.data || resp.data;
        if (!silent) toast.success('تم حفظ المسودة');
        if (saved?._id && aliveRef.current) {
          const next = cloneForm(saved);
          setForm(next);
          setBaseline(next);
          setMeta(saved);
        }
      }
      return saved;
    } catch (err) {
      toast.error(mapErrorToArabic(err, 'فشل الحفظ'));
      return null;
    } finally {
      if (aliveRef.current) setSaving(false);
    }
  }, [form, currentId, navigate, validateBeforeSave]);

  const publishPost = async () => {
    if (publishing) return;
    if (!validateBeforeSave()) return;
    setPublishing(true);
    try {
      let targetId = currentId;
      if (!targetId || dirty) {
        const saved = await saveDraft({ silent: true });
        if (!saved?._id) {
          setPublishing(false);
          return;
        }
        targetId = saved._id;
      }
      await blogAPI.publish(targetId);
      toast.success('تم نشر المقال');
      await fetchPost(targetId);
    } catch (err) {
      toast.error(mapErrorToArabic(err, 'فشل النشر'));
    } finally {
      if (aliveRef.current) setPublishing(false);
    }
  };

  const archivePost = async () => {
    if (archiving || !currentId) return;
    setArchiving(true);
    try {
      await blogAPI.archive(currentId);
      toast.success('تم أرشفة المقال');
      await fetchPost(currentId);
    } catch (err) {
      toast.error(err?.response?.data?.message || 'فشل الأرشفة');
    } finally {
      if (aliveRef.current) setArchiving(false);
    }
  };

  const deletePost = async () => {
    if (!currentId) {
      navigate('/settings/website/blog');
      return;
    }
    setDeleting(true);
    try {
      await blogAPI.remove(currentId);
      toast.success('تم حذف المقال');
      navigate('/settings/website/blog');
    } catch (err) {
      toast.error(err?.response?.data?.message || 'فشل الحذف');
      if (aliveRef.current) setDeleting(false);
    }
  };

  const setField = (patch) => setForm((prev) => ({ ...prev, ...patch }));
  const setNested = (key, subKey, value) =>
    setForm((prev) => ({ ...prev, [key]: { ...prev[key], [subKey]: value } }));

  const addTag = (raw) => {
    const t = String(raw || '').trim();
    if (!t) return;
    if (form.tags.includes(t)) return;
    if (form.tags.length >= 20) {
      toast.error('الحد الأقصى 20 وسماً');
      return;
    }
    setForm((prev) => ({ ...prev, tags: [...prev.tags, t] }));
    setTagInput('');
  };

  const removeTag = (t) => {
    setForm((prev) => ({ ...prev, tags: prev.tags.filter((x) => x !== t) }));
  };

  const onPickImage = (asset) => {
    const url = asset?.publicUrl || asset?.url || '';
    if (pickerField === 'featured') {
      setForm((prev) => ({ ...prev, featuredImageUrl: url }));
    } else if (pickerField === 'author') {
      setForm((prev) => ({ ...prev, author: { ...prev.author, avatarUrl: url } }));
    }
    setPickerField(null);
  };

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center" dir="rtl">
        <Loader2 className="w-10 h-10 animate-spin text-blue-600" />
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="container mx-auto px-6 py-10" dir="rtl">
        <div className="max-w-2xl mx-auto p-8 rounded-2xl border border-blue-100 bg-blue-50 text-center">
          <AlertTriangle className="w-10 h-10 mx-auto mb-2 text-blue-500" />
          <p className="text-sm text-blue-700">{loadError}</p>
          <div className="flex items-center justify-center gap-2 mt-4">
            <button
              onClick={() => currentId && fetchPost(currentId)}
              className="px-4 py-2 rounded-xl bg-blue-600 text-white text-sm"
            >
              أعد المحاولة
            </button>
            <button
              onClick={() => navigate('/settings/website/blog')}
              className="px-4 py-2 rounded-xl bg-white border border-gray-200 text-sm text-gray-700"
            >
              العودة للقائمة
            </button>
          </div>
        </div>
      </div>
    );
  }

  const contentLabel = contentLocale === 'ar' ? 'العربية' : 'English';
  const authorLabel = authorLocale === 'ar' ? 'العربية' : 'English';

  return (
    <div className="min-h-screen bg-[#F8FAFC]" dir="rtl">
      <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 py-6 lg:py-8">
        {/* Sticky status bar */}
        <div className="sticky top-0 z-20 -mx-4 sm:-mx-6 lg:-mx-8 px-4 sm:px-6 lg:px-8 py-3 bg-[#F8FAFC]/85 backdrop-blur border-b border-gray-100 mb-5">
          <div className="flex flex-wrap items-center gap-3">
            <button
              onClick={() => navigate('/settings/website/blog')}
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl text-sm font-medium bg-white border border-gray-200 text-gray-700 hover:bg-gray-50"
            >
              <ArrowRight className="w-4 h-4" />
              عودة
            </button>

            <div className="flex items-center gap-2 min-w-0">
              <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-blue-600 to-blue-700 flex items-center justify-center shadow-sm shrink-0">
                <FileText className="w-4.5 h-4.5 text-white" />
              </div>
              <div className="min-w-0">
                <h1 className="text-sm font-bold text-gray-800 truncate">
                  {form.title.ar || form.title.en || (currentId ? 'بدون عنوان' : 'مقال جديد')}
                </h1>
                <div className="flex items-center gap-2 text-xs text-gray-500">
                  <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-[10px] font-semibold ${statusBadge}`}>
                    {statusLabel}
                  </span>
                  {meta?.updatedAt && (
                    <span>آخر حفظ: {formatRelativeAr(meta.updatedAt)}</span>
                  )}
                </div>
              </div>
            </div>

            <div className="flex-1" />

            <button
              onClick={() => saveDraft()}
              disabled={saving || !dirty}
              className="inline-flex items-center gap-2 px-3 py-2 rounded-xl text-sm font-medium bg-blue-50 border border-blue-100 text-blue-700 hover:bg-blue-100 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              حفظ مسودة
            </button>

            <button
              type="button"
              disabled
              className="inline-flex items-center gap-2 px-3 py-2 rounded-xl text-sm font-medium bg-white border border-gray-200 text-gray-400 cursor-not-allowed"
              title="قيد التطوير"
            >
              <SearchIcon className="w-4 h-4" />
              معاينة (قريباً)
            </button>

            {meta?.status === 'published' ? (
              <button
                onClick={archivePost}
                disabled={archiving}
                className="inline-flex items-center gap-2 px-3 py-2 rounded-xl text-sm font-medium bg-white border border-gray-200 text-gray-700 hover:bg-gray-50 disabled:opacity-50"
              >
                {archiving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Archive className="w-4 h-4" />}
                أرشفة
              </button>
            ) : null}

            <button
              onClick={publishPost}
              disabled={publishing}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-bold bg-gradient-to-r from-emerald-600 to-emerald-700 text-white hover:from-emerald-700 hover:to-emerald-800 shadow-sm disabled:opacity-50"
            >
              {publishing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              نشر
            </button>

            <button
              onClick={() => setConfirmDelete(true)}
              className="p-2 rounded-xl border border-blue-200 text-blue-600 hover:bg-blue-50"
              title="حذف"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden mb-5">
          <div className="flex items-center gap-1 p-1.5 border-b border-gray-100 bg-gray-50/60">
            {[
              { value: 'content', label: 'المحتوى', icon: FileText },
              { value: 'seo', label: 'SEO', icon: SearchIcon },
              { value: 'settings', label: 'إعدادات', icon: Settings },
            ].map(({ value, label, icon: Icon }) => (
              <button
                key={value}
                type="button"
                onClick={() => setTab(value)}
                className={[
                  'flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors',
                  tab === value
                    ? 'bg-white text-blue-700 shadow-sm border border-gray-100'
                    : 'text-gray-600 hover:text-gray-800',
                ].join(' ')}
              >
                <Icon className="w-4 h-4" />
                {label}
              </button>
            ))}
          </div>
        </div>

        {/* Tab bodies */}
        {tab === 'content' && (
          <motion.div
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.15 }}
            className="space-y-5"
          >
            {/* Locale switch */}
            <div className="flex items-center gap-2">
              <div className="flex rounded-lg border border-gray-200 p-0.5 bg-white">
                {[
                  { value: 'ar', label: 'العربية' },
                  { value: 'en', label: 'English' },
                ].map((l) => (
                  <button
                    key={l.value}
                    type="button"
                    onClick={() => setContentLocale(l.value)}
                    className={[
                      'px-3 py-1 rounded-md text-xs font-medium transition-colors',
                      contentLocale === l.value ? 'bg-blue-600 text-white' : 'text-gray-600 hover:text-gray-800',
                    ].join(' ')}
                  >
                    {l.label}
                  </button>
                ))}
              </div>
              <span className="text-xs text-gray-400">جميع الحقول ثنائية اللغة</span>
            </div>

            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 space-y-5">
              {/* Title */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  العنوان ({contentLabel})
                </label>
                <input
                  value={form.title[contentLocale] || ''}
                  onChange={(e) => setNested('title', contentLocale, e.target.value)}
                  placeholder={contentLocale === 'ar' ? 'عنوان المقال' : 'Post title'}
                  dir={contentLocale === 'en' ? 'ltr' : 'rtl'}
                  className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                />
              </div>

              {/* Excerpt */}
              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <label className="block text-sm font-medium text-gray-700">
                    المقتطف ({contentLabel})
                  </label>
                  <span className={`text-[11px] font-mono ${
                    (form.excerpt[contentLocale] || '').length > 160 ? 'text-blue-500' : 'text-gray-400'
                  }`}>
                    {(form.excerpt[contentLocale] || '').length}/160
                  </span>
                </div>
                <textarea
                  value={form.excerpt[contentLocale] || ''}
                  onChange={(e) => setNested('excerpt', contentLocale, e.target.value)}
                  placeholder="ملخّص قصير يظهر في قوائم المقالات"
                  rows={3}
                  dir={contentLocale === 'en' ? 'ltr' : 'rtl'}
                  className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                />
              </div>

              {/* Content body */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  المحتوى ({contentLabel})
                </label>
                <textarea
                  value={form.content[contentLocale] || ''}
                  onChange={(e) => setNested('content', contentLocale, e.target.value)}
                  placeholder={contentLocale === 'ar' ? 'نص المقال بالكامل — يُسمح بـ HTML' : 'Full article text — HTML allowed'}
                  rows={16}
                  dir={contentLocale === 'en' ? 'ltr' : 'rtl'}
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm font-mono leading-7 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                />
                <p className="text-xs text-gray-400 mt-1">
                  يُسمح باستخدام وسوم HTML (h2, p, ul, a, strong, img...). المحرّر المرئي قريباً.
                </p>
              </div>
            </div>

            {/* Featured image */}
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 space-y-4">
              <div className="flex items-center gap-2">
                <ImageIcon className="w-4 h-4 text-blue-600" />
                <h3 className="text-sm font-bold text-gray-800">الصورة الرئيسية</h3>
              </div>

              {form.featuredImageUrl ? (
                <div className="flex items-start gap-3 p-3 rounded-xl border border-gray-200 bg-gray-50">
                  <img
                    src={form.featuredImageUrl}
                    alt={form.featuredImageAlt || 'featured'}
                    className="w-28 h-28 object-cover rounded-lg shrink-0"
                  />
                  <div className="flex-1 min-w-0">
                    <div className="text-xs text-gray-500 font-mono truncate" dir="ltr">
                      {form.featuredImageUrl}
                    </div>
                    <div className="flex items-center gap-2 mt-2">
                      <button
                        type="button"
                        onClick={() => setPickerField('featured')}
                        className="px-3 py-1.5 rounded-lg text-xs font-medium bg-white border border-gray-200 hover:border-blue-300 hover:text-blue-700"
                      >
                        تغيير
                      </button>
                      <button
                        type="button"
                        onClick={() => setField({ featuredImageUrl: '' })}
                        className="px-3 py-1.5 rounded-lg text-xs font-medium bg-white border border-gray-200 hover:border-blue-300 hover:text-blue-600"
                      >
                        إزالة
                      </button>
                    </div>
                  </div>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => setPickerField('featured')}
                  className="w-full flex flex-col items-center justify-center gap-2 p-6 rounded-xl border-2 border-dashed border-gray-200 bg-gray-50 hover:border-blue-300 hover:bg-blue-50/40 transition-colors text-sm text-gray-500"
                >
                  <ImageIcon className="w-6 h-6 text-gray-400" />
                  اختر صورة من المكتبة
                </button>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">النص البديل (alt)</label>
                <input
                  value={form.featuredImageAlt}
                  onChange={(e) => setField({ featuredImageAlt: e.target.value })}
                  placeholder="وصف قصير للصورة — مهم للـ SEO وقارئات الشاشة"
                  className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">وقت القراءة التقريبي (دقيقة)</label>
                <input
                  type="number"
                  min={0}
                  value={form.readTimeMinutes}
                  onChange={(e) => setField({ readTimeMinutes: e.target.value })}
                  className="w-full max-w-xs px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                />
              </div>
            </div>
          </motion.div>
        )}

        {tab === 'seo' && (
          <PageSeoPanel
            value={form.seo}
            onChange={(v) => setForm((prev) => ({ ...prev, seo: v }))}
            pageLabel={form.title.ar || form.title.en || 'مقال جديد'}
            pageKey="blog-post"
            dirty={seoDirty}
            saving={saving}
            onSave={() => saveDraft()}
          />
        )}

        {tab === 'settings' && (
          <motion.div
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.15 }}
            className="space-y-5"
          >
            {/* Featured + sort order */}
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 space-y-4">
              <h3 className="text-sm font-bold text-gray-800">الترتيب والإبراز</h3>
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={!!form.isFeatured}
                  onChange={(e) => setField({ isFeatured: e.target.checked })}
                  className="mt-1 w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500/20"
                />
                <span className="flex-1">
                  <span className="block text-sm font-semibold text-gray-800">مقال مميّز</span>
                  <span className="block text-xs text-gray-500 mt-1">
                    يظهر في المقدّمة على صفحة المدوّنة وفي القسم البارز في الصفحة الرئيسية.
                  </span>
                </span>
              </label>
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1.5">
                  ترتيب المقال (رقم أصغر = ظاهر أولاً)
                </label>
                <input
                  type="number"
                  value={form.sortOrder ?? 100}
                  onChange={(e) => setField({ sortOrder: Number(e.target.value) || 100 })}
                  className="w-32 px-3 py-2 border border-gray-200 rounded-xl text-sm font-mono focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                />
              </div>
            </div>

            {/* Slug */}
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
              <h3 className="text-sm font-bold text-gray-800 mb-3">رابط المقال (slug)</h3>
              <input
                value={form.slug}
                onChange={(e) => setField({ slug: e.target.value })}
                placeholder="اتركه فارغاً ليُنشأ تلقائياً من العنوان"
                dir="ltr"
                className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm font-mono focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
              />
              {meta?.status === 'published' && (
                <p className="text-xs text-amber-700 bg-amber-50 border border-amber-100 rounded-lg p-2 mt-2">
                  لا ينصح بتغيير الرابط بعد النشر — سيُكسر أي ارتباط خارجي.
                </p>
              )}
              <p className="text-xs text-gray-400 mt-2">
                حروف عربية أو إنجليزية صغيرة، أرقام وشرطات فقط. مثال: <code dir="ltr">how-to-read-cbc</code>
              </p>
            </div>

            {/* Category */}
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-bold text-gray-800">التصنيف</h3>
                <button
                  type="button"
                  onClick={() => setCategoriesOpen(true)}
                  className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-medium bg-blue-50 text-blue-700 hover:bg-blue-100"
                >
                  <Plus className="w-3.5 h-3.5" />
                  إدارة التصنيفات
                </button>
              </div>
              <select
                value={form.categoryId || ''}
                onChange={(e) => setField({ categoryId: e.target.value })}
                className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
              >
                <option value="">بدون تصنيف</option>
                {categories.map((c) => (
                  <option key={c._id} value={c._id}>
                    {c.name?.ar || c.name?.en || c.slug}
                  </option>
                ))}
              </select>
            </div>

            {/* Tags */}
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
              <div className="flex items-center gap-2 mb-3">
                <TagIcon className="w-4 h-4 text-blue-600" />
                <h3 className="text-sm font-bold text-gray-800">الوسوم</h3>
              </div>
              <div className="flex flex-wrap gap-2 items-center p-2 rounded-xl border border-gray-200 min-h-[44px] bg-white">
                {form.tags.map((t) => (
                  <span
                    key={t}
                    className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg bg-blue-50 text-blue-700 text-xs font-medium border border-blue-100"
                  >
                    {t}
                    <button
                      type="button"
                      onClick={() => removeTag(t)}
                      className="p-0.5 rounded hover:bg-blue-100"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                ))}
                <div className="flex items-center gap-1 flex-1 min-w-[180px]">
                  <input
                    value={tagInput}
                    onChange={(e) => setTagInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ',' || e.key === 'Tab') {
                        e.preventDefault();
                        addTag(tagInput);
                      }
                    }}
                    placeholder="أضف وسماً ثم Enter"
                    className="flex-1 px-2 py-1 bg-transparent outline-none text-sm placeholder:text-gray-400"
                  />
                  <button
                    type="button"
                    onClick={() => addTag(tagInput)}
                    disabled={!tagInput.trim()}
                    className="p-1 rounded-md text-gray-400 hover:text-blue-600 hover:bg-blue-50 disabled:opacity-40"
                  >
                    <Plus className="w-4 h-4" />
                  </button>
                </div>
              </div>
              <p className="text-xs text-gray-400 mt-2">
                الحد الأقصى 20 وسماً. استخدم كلمات قصيرة لمساعدة الزوّار في التصفية.
              </p>
            </div>

            {/* Author */}
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 space-y-4">
              <div className="flex items-center gap-2">
                <User className="w-4 h-4 text-blue-600" />
                <h3 className="text-sm font-bold text-gray-800">بيانات الكاتب</h3>
              </div>

              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">الاسم</label>
                  <input
                    value={form.author.name}
                    onChange={(e) => setForm((prev) => ({ ...prev, author: { ...prev.author, name: e.target.value } }))}
                    placeholder="اسم الكاتب"
                    className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">صورة الكاتب</label>
                  {form.author.avatarUrl ? (
                    <div className="flex items-center gap-3 p-2 rounded-xl border border-gray-200 bg-gray-50">
                      <img
                        src={form.author.avatarUrl}
                        alt={form.author.name || 'author'}
                        className="w-12 h-12 object-cover rounded-full shrink-0"
                      />
                      <div className="flex items-center gap-2 flex-1 min-w-0">
                        <button
                          type="button"
                          onClick={() => setPickerField('author')}
                          className="px-3 py-1.5 rounded-lg text-xs font-medium bg-white border border-gray-200 hover:border-blue-300 hover:text-blue-700"
                        >
                          تغيير
                        </button>
                        <button
                          type="button"
                          onClick={() => setForm((prev) => ({ ...prev, author: { ...prev.author, avatarUrl: '' } }))}
                          className="px-3 py-1.5 rounded-lg text-xs font-medium bg-white border border-gray-200 hover:border-blue-300 hover:text-blue-600"
                        >
                          إزالة
                        </button>
                      </div>
                    </div>
                  ) : (
                    <button
                      type="button"
                      onClick={() => setPickerField('author')}
                      className="w-full flex items-center gap-2 px-4 py-2.5 rounded-xl border border-dashed border-gray-200 bg-gray-50 hover:border-blue-300 hover:bg-blue-50/40 text-sm text-gray-500"
                    >
                      <ImageIcon className="w-4 h-4" />
                      اختر من المكتبة
                    </button>
                  )}
                </div>
              </div>

              {/* Bio — bilingual */}
              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <label className="block text-sm font-medium text-gray-700">
                    نبذة ({authorLabel})
                  </label>
                  <div className="flex rounded-md border border-gray-200 p-0.5 bg-gray-50">
                    {[
                      { value: 'ar', label: 'AR' },
                      { value: 'en', label: 'EN' },
                    ].map((l) => (
                      <button
                        key={l.value}
                        type="button"
                        onClick={() => setAuthorLocale(l.value)}
                        className={[
                          'px-2 py-0.5 rounded-sm text-[11px] font-semibold',
                          authorLocale === l.value ? 'bg-white text-blue-700 shadow-sm' : 'text-gray-500',
                        ].join(' ')}
                      >
                        {l.label}
                      </button>
                    ))}
                  </div>
                </div>
                <textarea
                  value={form.author.bio[authorLocale] || ''}
                  onChange={(e) => setForm((prev) => ({
                    ...prev,
                    author: {
                      ...prev.author,
                      bio: { ...prev.author.bio, [authorLocale]: e.target.value },
                    },
                  }))}
                  placeholder={authorLocale === 'ar' ? 'تعريف مختصر بالكاتب' : 'Brief author intro'}
                  rows={3}
                  dir={authorLocale === 'en' ? 'ltr' : 'rtl'}
                  className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                />
              </div>
            </div>
          </motion.div>
        )}
      </div>

      {/* Media picker */}
      <MediaPicker
        open={!!pickerField}
        onClose={() => setPickerField(null)}
        onPick={onPickImage}
        folder={pickerField === 'author' ? 'team' : 'blog'}
      />

      {/* Categories manager (from settings tab) */}
      <BlogCategories
        open={categoriesOpen}
        onClose={() => setCategoriesOpen(false)}
        onChanged={fetchCategories}
      />

      {/* Confirm delete */}
      {confirmDelete && (
        <div
          className="fixed inset-0 z-50 bg-gray-900/60 flex items-center justify-center p-4"
          onClick={() => setConfirmDelete(false)}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="bg-white rounded-2xl p-6 max-w-md w-full shadow-xl"
          >
            <div className="flex items-start gap-3 mb-4">
              <div className="w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center shrink-0">
                <AlertTriangle className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <h3 className="text-base font-bold text-gray-800">تأكيد حذف المقال</h3>
                <p className="text-sm text-gray-600 mt-1">
                  سيتم حذف المقال نهائياً. لا يمكن التراجع.
                </p>
              </div>
            </div>
            <div className="flex items-center justify-end gap-2">
              <button
                onClick={() => setConfirmDelete(false)}
                className="px-4 py-2 rounded-xl text-sm font-medium text-gray-600 hover:bg-gray-50"
              >
                إلغاء
              </button>
              <button
                onClick={deletePost}
                disabled={deleting}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50"
              >
                {deleting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                حذف
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
